import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, BookOpen } from "lucide-react";

const PRINT_CSS = `
@media print {
  header, nav, .no-print { display: none !important; }
  .manuale-wrap { max-width: 100% !important; padding: 0 !important; margin: 0 !important; }
  .scheda { break-inside: avoid; box-shadow: none !important; border-color: #cbd5e1 !important; }
  body { background: #fff !important; }
}
`;

export function ManualeLayout({ titolo, sottotitolo, icona: Icona = BookOpen, accent = "#0d9488", sezioni, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <style>{PRINT_CSS}</style>
      <Header />
      <main className="manuale-wrap max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} className="no-print flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Indietro</button>
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}><Icona className="h-6 w-6" /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">Manuale operatore</div>
              <h1 className="font-heading text-3xl font-medium text-slate-900">{titolo}</h1>
              {sottotitolo && <p className="text-slate-500 text-sm">{sottotitolo}</p>}
            </div>
          </div>
          <Button className="no-print" onClick={() => window.print()} style={{ backgroundColor: accent }} data-testid="manuale-print-btn"><Printer className="h-4 w-4 mr-2" /> Stampa / Salva PDF</Button>
        </div>

        {sezioni && sezioni.length > 0 && (
          <nav className="no-print rounded-xl border border-slate-200 bg-white p-4 mb-8" data-testid="manuale-indice">
            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Indice</div>
            <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1 list-decimal list-inside text-sm text-slate-700">
              {sezioni.map((s) => <li key={s.id}><a href={`#${s.id}`} className="hover:underline" style={{ color: accent }}>{s.titolo}</a></li>)}
            </ol>
          </nav>
        )}

        <div className="space-y-6">{children}</div>

        <p className="text-xs text-slate-400 mt-10 no-print">Documento generato dal sistema Assietica Suite. Contenuti allineati alle funzioni attualmente disponibili nell'app.</p>
      </main>
    </div>
  );
}

export const Scheda = ({ id, n, titolo, accent = "#0d9488", children }) => (
  <section id={id} className="scheda rounded-xl border border-slate-200 bg-white p-6 shadow-sm scroll-mt-24">
    <h2 className="font-heading text-xl text-slate-900 mb-3 flex items-center gap-2">
      {n != null && <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-sm font-semibold" style={{ backgroundColor: `${accent}1A`, color: accent }}>{n}</span>}
      {titolo}
    </h2>
    <div className="space-y-3 text-sm text-slate-700 leading-relaxed">{children}</div>
  </section>
);

export const P = ({ children }) => <p>{children}</p>;

export const Steps = ({ items }) => (
  <ol className="space-y-1.5 list-decimal list-inside marker:text-slate-400">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ol>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-1.5 list-disc list-inside marker:text-slate-300">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

export const Callout = ({ tipo = "info", children }) => {
  const styles = {
    info: "bg-sky-50 border-sky-200 text-sky-800",
    warn: "bg-amber-50 border-amber-200 text-amber-800",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-800",
  }[tipo];
  return <div className={`rounded-lg border p-3 text-sm ${styles}`}>{children}</div>;
};

export const FAQ = ({ items }) => (
  <div className="divide-y divide-slate-100">
    {items.map((f, i) => (
      <div key={i} className="py-3">
        <div className="font-medium text-slate-800">{f.q}</div>
        <div className="text-sm text-slate-600 mt-1">{f.a}</div>
      </div>
    ))}
  </div>
);
